// Shared serialize component for text input editors (SingleLineText, MultiLineText)

import { isEmptyString } from '@usertour/helpers';
import { memo, useCallback, useMemo, useState } from 'react';
import type { ChangeEvent } from 'react';

import { Button, Input, Textarea } from '../../primitives';
import { useWidgetLocale } from '../../locale/context';

// Constants
const DEFAULT_PLACEHOLDER = 'Enter text...';
const DEFAULT_BUTTON_TEXT = 'Submit';

// Types
export interface TextInputElementData {
  placeholder?: string;
  buttonText?: string;
  required?: boolean;
}

export interface TextInputSerializeProps<T extends { data: TextInputElementData }> {
  element: T;
  onClick?: (element: T, value: string) => Promise<void> | void;
  // Use 'input' or 'textarea' to specify the input type
  inputType: 'input' | 'textarea';
  // Optional class name for the input component
  inputClassName?: string;
}

// Generic text input serialize component
function TextInputSerializeInner<T extends { data: TextInputElementData }>({
  element,
  onClick,
  inputType,
  inputClassName,
}: TextInputSerializeProps<T>) {
  const { messages } = useWidgetLocale();
  const [value, setValue] = useState<string>('');
  const [loading, setLoading] = useState(false);

  // Memoize computed values
  const isDisabled = useMemo(
    () => loading || (element.data.required && isEmptyString(value)),
    [loading, element.data.required, value],
  );

  const defaultValues = useMemo(
    () => ({
      placeholder: element.data.placeholder || messages.enterText,
      buttonText: element.data.buttonText || messages.submit,
    }),
    [element.data.placeholder, element.data.buttonText, messages],
  );

  const handleValueChange = useCallback(
    (e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement>) => {
      setValue(e.target.value);
    },
    [],
  );

  const handleSubmit = useCallback(async () => {
    if (onClick) {
      setLoading(true);
      try {
        await onClick(element, value);
      } finally {
        setLoading(false);
      }
    }
  }, [onClick, element, value]);

  // Render the appropriate input component
  const InputComponent = inputType === 'textarea' ? Textarea : Input;
  const inputProps = inputType === 'input' ? { className: inputClassName || 'grow h-auto' } : {};

  return (
    <div className="flex flex-col gap-2 items-center w-full">
      <InputComponent
        placeholder={defaultValues.placeholder}
        value={value}
        onChange={handleValueChange}
        aria-label={messages.textInput}
        {...inputProps}
      />
      <div className="flex justify-end w-full">
        <Button
          className={inputType === 'input' ? 'flex-none' : undefined}
          onClick={handleSubmit}
          disabled={isDisabled}
          aria-label={defaultValues.buttonText}
        >
          {defaultValues.buttonText}
        </Button>
      </div>
    </div>
  );
}

// Memoized version with generic support
export const TextInputSerialize = memo(TextInputSerializeInner) as typeof TextInputSerializeInner;

// Export constants for use in other components
export { DEFAULT_BUTTON_TEXT, DEFAULT_PLACEHOLDER };
